package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.model.GiverLite
import com.dotdrives.model.NewReport
import com.dotdrives.model.Report
import com.dotdrives.model.ReportDefaultUpdate
import com.dotdrives.model.ReportFileItem
import com.dotdrives.model.ReportStatus
import com.dotdrives.model.ReportUpdate
import com.dotdrives.model.ShareAudience
import com.dotdrives.model.Target
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.FilterOperator
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * `reports`/`report_updates` are the one table pair `field` can insert/update into (via
 * `has_report_write`, not `has_perm`) -- see docs/schema.md "Controller ruling: field-role
 * writes". This client issues those calls with no role branching of its own; the server-side
 * RLS predicate is the actual enforcement. `report_recipients` (share) and
 * `report_default_updates` (templates) stay staff-only the same way -- callers are expected to
 * gate those actions in the UI, per the same ruling.
 *
 * [targetAudience] does read `giver_id`/`target_id` off `donations` (and an active-status flag
 * off `giving_subscriptions`) for the share dialog's audience presets -- never an amount, and
 * never for a caller [UpdatesController] would let a field rep reach: both audience methods and
 * `sendToGivers` itself are only ever invoked from the staff-gated share flow (see
 * `UpdatesController.startShare`/`confirmShare`), matching `report_recipients_insert`'s
 * `has_perm(org_id,'report.write')` policy, which excludes `field` outright. No donation
 * *amount*, and no query keyed only on money, appears anywhere in this file -- that distinction
 * is what stays on the separate, always-staff [GivingSummaryClient] instead.
 */
class ReportClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val authClient: AuthClient = AuthClient(client),
) {
    suspend fun boardPage(
        orgId: String, cursor: com.dotdrives.model.UpdatesCursor? = null,
        filters: com.dotdrives.controller.UpdatesFilters = com.dotdrives.controller.UpdatesFilters(),
    ): com.dotdrives.model.UpdatesBoardPage = client.postgrest.rpc("updates_board_page", buildJsonObject {
        put("p_org", orgId)
        put("p_before_created", cursor?.createdAt?.toString()); put("p_before_id", cursor?.id)
        put("p_unread", filters.onlyUnread); put("p_assignee", filters.assigneeUserId)
        put("p_status", filters.status); put("p_target", filters.targetId); put("p_query", filters.query)
    }).decodeAs()

    suspend fun get(orgId: String, reportId: String): Report {
        val row = client.from(REPORTS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", reportId)
                }
            }
            .decodeSingle<ReportRow>()
        val targetName = row.targetId?.let { loadTargetNames(orgId, listOf(it))[it] }
        val assigneeName = loadUserNames(listOf(row.assigneeUserId))[row.assigneeUserId]
        return row.toReport(targetName, assigneeName)
    }

    suspend fun create(orgId: String, report: NewReport): Report {
        val id = uuidV7()
        val actor = authClient.requireCurrentUserId()
        client.from(REPORTS_TABLE).insert(
            ReportInsert(
                id = id,
                orgId = orgId,
                title = report.title,
                targetId = report.targetId,
                assigneeUserId = report.assigneeUserId,
                createdBy = actor,
            ),
        )
        return get(orgId, id)
    }

    suspend fun thread(orgId: String, reportId: String): List<ReportUpdate> {
        val rows = client.from(REPORT_UPDATES_TABLE)
            .select {
                order("created_at", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("report_id", reportId)
                }
            }
            .decodeList<ReportUpdateRow>()
        if (rows.isEmpty()) return emptyList()
        val authorNames = loadUserNames(rows.mapNotNull(ReportUpdateRow::authorUserId).distinct())
        return rows.map { it.toReportUpdate(authorNames[it.authorUserId]) }
    }

    suspend fun postUpdate(orgId: String, reportId: String, body: String): ReportUpdate {
        val id = uuidV7()
        val actor = authClient.requireCurrentUserId()
        client.from(REPORT_UPDATES_TABLE).insert(
            ReportUpdateInsert(
                id = id,
                orgId = orgId,
                reportId = reportId,
                body = body,
                authorUserId = actor,
            ),
        )
        val row = client.from(REPORT_UPDATES_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", id)
                }
            }
            .decodeSingle<ReportUpdateRow>()
        val authorName = loadUserNames(listOf(actor))[actor]
        return row.toReportUpdate(authorName)
    }

    /**
     * `submitted_at` is not trigger-maintained (docs/schema.md: "the client is expected to set
     * it explicitly in the same update") -- set only on the transition into `submitted`, and
     * left untouched on every other transition (e.g. `submitted` -> `shared`).
     */
    suspend fun setStatus(orgId: String, reportId: String, status: String): Report {
        client.from(REPORTS_TABLE).update(
            ReportStatusUpdate(
                status = status,
                submittedAt = if (status == ReportStatus.SUBMITTED) Clock.System.now() else null,
            ),
        ) {
            filter {
                eq("org_id", orgId)
                eq("id", reportId)
            }
        }
        return get(orgId, reportId)
    }

    suspend fun defaultUpdates(orgId: String): List<ReportDefaultUpdate> =
        client.from(DEFAULT_UPDATES_TABLE)
            .select {
                order("subject", Order.ASCENDING)
                filter { eq("org_id", orgId) }
            }
            .decodeList<ReportDefaultUpdateRow>()
            .map { ReportDefaultUpdate(it.id, it.subject, it.message) }

    suspend fun files(orgId: String, reportId: String): List<ReportFileItem> =
        client.from(FILES_TABLE)
            .select {
                order("created_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("owner_type", REPORT_OWNER_TYPE)
                    eq("owner_id", reportId)
                }
            }
            .decodeList<ReportAttachedFileRow>()
            .map { ReportFileItem(it.id, it.fileName, it.fileSize, it.createdAt) }

    suspend fun targets(orgId: String): List<Target> =
        client.from(TARGETS_TABLE)
            .select {
                order("name", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("is_active", true)
                }
            }
            .decodeList()

    suspend fun givers(orgId: String): List<GiverLite> =
        client.from(GIVERS_TABLE)
            .select {
                order("display_name", Order.ASCENDING)
                filter { eq("org_id", orgId) }
            }
            .decodeList<GiverLiteRow>()
            .map { GiverLite(it.id, it.displayName) }

    /**
     * The share dialog's "All &lt;target&gt; givers" preset. Distinct givers with a donation
     * designated to [targetId], plus how many of those already have an active recurring gift
     * ("includes 38 monthly partners" in the design copy). Reads `donations`/
     * `giving_subscriptions` directly -- both `has_perm_strict('donation.read')`-gated, but this
     * method is only ever called from [UpdatesController.startShare], which is staff-gated the
     * same way `sendToGivers` is (see the docstring on [ReportRepository.sendToGivers]).
     */
    suspend fun targetAudience(orgId: String, targetId: String): ShareAudience {
        val giverIds = client.from(DONATIONS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("target_id", targetId)
                }
            }
            .decodeList<GiverIdRow>()
            .map(GiverIdRow::giverId)
            .distinct()
        if (giverIds.isEmpty()) return ShareAudience(emptyList(), 0)
        val monthlyPartnerCount = client.from(SUBSCRIPTIONS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("status", "active")
                    isIn("giver_id", giverIds)
                }
            }
            .decodeList<GiverIdRow>()
            .map(GiverIdRow::giverId)
            .distinct()
            .size
        return ShareAudience(giverIds, monthlyPartnerCount)
    }

    /** The share dialog's "Only givers on active drives" preset -- distinct givers on a
     *  `drives` row with no `outcome` and not archived. Same staff-only-caller reasoning as
     *  [targetAudience]. */
    suspend fun activeDrivesAudience(orgId: String): ShareAudience {
        val giverIds = client.from(DRIVES_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    filter("outcome", FilterOperator.IS, null)
                    filter("archived_at", FilterOperator.IS, null)
                }
            }
            .decodeList<GiverIdRow>()
            .map(GiverIdRow::giverId)
            .distinct()
        return ShareAudience(giverIds, 0)
    }

    suspend fun sendToGivers(orgId: String, reportId: String, giverIds: List<String>): Int {
        if (giverIds.isEmpty()) return 0
        return client.postgrest.rpc("share_report", kotlinx.serialization.json.buildJsonObject {
            put("p_org", kotlinx.serialization.json.JsonPrimitive(orgId))
            put("p_report", kotlinx.serialization.json.JsonPrimitive(reportId))
            put("p_recipients", kotlinx.serialization.json.JsonArray(giverIds.distinct().map { giverId ->
                kotlinx.serialization.json.buildJsonObject { put("id", kotlinx.serialization.json.JsonPrimitive(uuidV7())); put("giver_id", kotlinx.serialization.json.JsonPrimitive(giverId)) }
            }))
        }).decodeAs<Int>()
    }

    suspend fun markRead(orgId: String, reportId: String) {
        val userId = authClient.requireCurrentUserId()
        client.from(READ_STATES_TABLE).upsert(
            ReadStateUpsert(
                orgId = orgId,
                userId = userId,
                subjectType = REPORT_SUBJECT_TYPE,
                subjectId = reportId,
            ),
        )
    }

    private suspend fun loadTargetNames(orgId: String, targetIds: List<String>): Map<String, String> {
        if (targetIds.isEmpty()) return emptyMap()
        return client.from(TARGETS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    isIn("id", targetIds)
                }
            }
            .decodeList<ReportTargetNameRow>()
            .associate { it.id to it.name }
    }

    private suspend fun loadUserNames(userIds: List<String>): Map<String, String> {
        if (userIds.isEmpty()) return emptyMap()
        return client.from(PROFILES_TABLE)
            .select {
                filter { isIn("user_id", userIds) }
            }
            .decodeList<ReportProfileNameRow>()
            .associate { profile ->
                profile.userId to listOf(profile.firstName, profile.lastName)
                    .filter(String::isNotBlank)
                    .joinToString(" ")
            }
    }
}

private fun ReportRow.toReport(targetName: String?, assigneeName: String?): Report =
    Report(
        id = id,
        orgId = orgId,
        title = title,
        targetId = targetId,
        targetName = targetName,
        assigneeUserId = assigneeUserId,
        assigneeName = assigneeName,
        status = status,
        dueAt = dueAt,
        submittedAt = submittedAt,
        createdAt = createdAt,
    )

private fun ReportUpdateRow.toReportUpdate(authorName: String?): ReportUpdate =
    ReportUpdate(
        id = id,
        reportId = reportId,
        body = body,
        authorUserId = authorUserId,
        authorName = authorName,
        viaEmail = viaEmail,
        createdAt = createdAt,
    )

@Serializable
private data class ReportRow(
    val id: String,
    @SerialName("org_id") val orgId: String,
    val title: String,
    @SerialName("target_id") val targetId: String?,
    @SerialName("assignee_user_id") val assigneeUserId: String,
    val status: String,
    @SerialName("due_at") val dueAt: Instant?,
    @SerialName("submitted_at") val submittedAt: Instant?,
    @SerialName("created_at") val createdAt: Instant,
)

@Serializable
private data class ReportInsert(
    val id: String,
    @SerialName("org_id") val orgId: String,
    val title: String,
    @SerialName("target_id") val targetId: String?,
    @SerialName("assignee_user_id") val assigneeUserId: String,
    @SerialName("created_by") val createdBy: String,
)

@Serializable
private data class ReportStatusUpdate(
    val status: String,
    @SerialName("submitted_at") val submittedAt: Instant?,
)

@Serializable
private data class ReportUpdateRow(
    val id: String,
    @SerialName("report_id") val reportId: String,
    val body: String,
    @SerialName("author_user_id") val authorUserId: String?,
    @SerialName("via_email") val viaEmail: Boolean,
    @SerialName("created_at") val createdAt: Instant,
)

@Serializable
private data class ReportUpdateInsert(
    val id: String,
    @SerialName("org_id") val orgId: String,
    @SerialName("report_id") val reportId: String,
    val body: String,
    @SerialName("author_user_id") val authorUserId: String,
)

@Serializable
private data class ReportDefaultUpdateRow(
    val id: String,
    val subject: String,
    val message: String,
)

@Serializable
private data class ReportAttachedFileRow(
    val id: String,
    @SerialName("file_name") val fileName: String,
    @SerialName("file_size") val fileSize: Long,
    @SerialName("created_at") val createdAt: Instant,
)

@Serializable
private data class ReportFileOwnerRow(
    @SerialName("owner_id") val ownerId: String,
)

@Serializable
private data class ReportTargetNameRow(
    val id: String,
    val name: String,
)

@Serializable
private data class ReportProfileNameRow(
    @SerialName("user_id") val userId: String,
    @SerialName("first_name") val firstName: String,
    @SerialName("last_name") val lastName: String,
)

@Serializable
private data class GiverLiteRow(
    val id: String,
    @SerialName("display_name") val displayName: String,
)

@Serializable
private data class GiverIdRow(
    @SerialName("giver_id") val giverId: String,
)

@Serializable
private data class ReportRecipientInsert(
    val id: String,
    @SerialName("org_id") val orgId: String,
    @SerialName("report_id") val reportId: String,
    @SerialName("giver_id") val giverId: String,
)

@Serializable
private data class ReadStateUpsert(
    @SerialName("org_id") val orgId: String,
    @SerialName("user_id") val userId: String,
    @SerialName("subject_type") val subjectType: String,
    @SerialName("subject_id") val subjectId: String,
)

@Serializable
private data class ReadStateSubjectRow(
    @SerialName("subject_id") val subjectId: String,
)

private const val REPORTS_TABLE = "reports"
private const val REPORT_UPDATES_TABLE = "report_updates"
private const val DEFAULT_UPDATES_TABLE = "report_default_updates"
private const val RECIPIENTS_TABLE = "report_recipients"
private const val READ_STATES_TABLE = "read_states"
private const val FILES_TABLE = "files"
private const val TARGETS_TABLE = "targets"
private const val GIVERS_TABLE = "givers"
private const val PROFILES_TABLE = "profiles"
private const val DONATIONS_TABLE = "donations"
private const val DRIVES_TABLE = "drives"
private const val SUBSCRIPTIONS_TABLE = "giving_subscriptions"
private const val REPORT_OWNER_TYPE = "report"
private const val REPORT_SUBJECT_TYPE = "report"
